import React, { useMemo, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';

import Button from '@components/Button';

import registries from '@constants/registries';

import RegistrySelect from './RegistrySelect';
import CheckboxList from './CheckboxList';

import * as styles from './SearchBar.module.css';

const SearchBar = ({
  onSearch,
  onChange,
  state = {
    registry: 'all',
    searchTerm: '',
    licenses: [],
    keywords: [],
  },
  hasAdvancedFilters = false,
  hasRegistrySelector = true,
  searching = false,
  initiallyOpenAdvancedFilters = false,
  licenses = [],
  keywords = [],
}) => {
  const hasActiveFilters = useMemo(() => {
    return (
      state &&
      ((state.licenses && state.licenses?.length > 0) ||
        (state.keywords && state.keywords?.length > 0))
    );
  }, [state]);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    initiallyOpenAdvancedFilters,
  );

  const handleChange = (update, trigger = false) => {
    const newState = {
      ...state,
      ...update,
    };

    onChange?.(newState, trigger);
  };

  const handleCheckboxChange = (namespace, name, value) => {
    const oldNamespace = state[namespace] ?? [];

    const newNamespace = value
      ? [...oldNamespace, name]
      : oldNamespace.filter((n) => n !== name);

    const newState = {
      ...state,
      [namespace]: newNamespace,
    };

    onChange?.(newState, true);
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      onSearch(state);
    }
  };

  const handleSubmit = () => {
    onSearch(state);
  };

  const selectableLicenses = useMemo(() => {
    const items =
      licenses && licenses.length > 0 ? licenses : state?.licenses ?? [];

    return items.map((l) => ({
      label: l,
      value: state.licenses?.length > 0 && state.licenses.includes(l),
    }));
  }, [licenses, state]);

  const selectableKeywords = useMemo(() => {
    const items =
      keywords && keywords.length > 0 ? keywords : state?.keywords ?? [];

    return items.map((k) => ({
      label: k,
      value: state.keywords?.length > 0 && state.keywords.includes(k),
    }));
  }, [keywords, state]);

  const resetAdvancedFilters = () => {
    const newState = {
      ...state,
      keywords: [],
      licenses: [],
    };

    onChange?.(newState, true);
  };

  return (
    <Collapsible.Root
      className={styles.root}
      open={showAdvancedFilters}
      onOpenChange={setShowAdvancedFilters}
    >
      <div className={styles.wrapper}>
        <div className={styles.inputs}>
          {hasRegistrySelector && (
            <RegistrySelect
              value={state?.registry ?? 'all'}
              className={styles.select}
              registries={registries.map((r) => ({
                value: r.key,
                label: r.name,
                icon: r.iconWithoutColor ?? r.icon,
              }))}
              onChange={(val) => {
                handleChange({ registry: val }, hasAdvancedFilters);
              }}
            />
          )}
          <input
            className={styles.searchField}
            type="text"
            placeholder="Enter search term"
            /* As we might get passed a nullish value
             * but null is not a valid value for an input
             * tag, let's be forgiving about this. */
            value={state?.searchTerm ?? ''}
            onChange={(e) => handleChange({ searchTerm: e.target.value })}
            onKeyUp={handleKeyUp}
          />
        </div>
        <nav className={styles.actions}>
          {hasAdvancedFilters && (
            <Collapsible.Trigger asChild>
              <Button
                variant="link"
                className={styles.toggleFiltersButton}
                size="small"
              >
                {!showAdvancedFilters ? 'Show' : 'Hide'} Filters
              </Button>
            </Collapsible.Trigger>
          )}
          <Button
            className={styles.submitButton}
            disabled={searching}
            onClick={handleSubmit}
          >
            {searching ? 'Loading' : 'Search'}
          </Button>
        </nav>
      </div>
      <Collapsible.Content className={styles.advancedFiltersContainer}>
        {hasActiveFilters && (
          <Button
            className={styles.resetAdvancedFilters}
            onClick={resetAdvancedFilters}
            variant="link"
            size="small"
          >
            Reset
          </Button>
        )}

        <div className={styles.advancedFilters}>
          <CheckboxList
            label="Filter by Licenses"
            items={selectableLicenses}
            onChange={(n, v) => handleCheckboxChange('licenses', n, v)}
          />

          <CheckboxList
            label="Filter by Keywords"
            items={selectableKeywords}
            onChange={(n, v) => handleCheckboxChange('keywords', n, v)}
          />
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export default SearchBar;
